import { useState } from "react";
import { useTranslation } from "react-i18next";
import { trpc } from "../../lib/trpc.js";
import { Button } from "../../components/ui/Button.js";

interface Props {
  clubBookId: string;
  currentPage: number | null;
  currentTotalPages: number | null;
  canonicalPageCount: number | null;
}

export function ProgressUpdater({ clubBookId, currentPage, currentTotalPages, canonicalPageCount }: Props) {
  const [page, setPage] = useState(String(currentPage ?? ""));
  const [totalPages, setTotalPages] = useState(
    String(currentTotalPages ?? canonicalPageCount ?? "")
  );
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  const update = trpc.progress.update.useMutation({
    onSuccess: () => {
      utils.progress.list.invalidate({ clubBookId });
      utils.comments.list.invalidate({ clubBookId });
    },
  });

  const effectiveTotalPages = parseInt(totalPages, 10);
  const hasValidTotal = !isNaN(effectiveTotalPages) && effectiveTotalPages > 0;

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        const p = parseInt(page, 10);
        if (!isNaN(p) && p >= 0) {
          update.mutate({
            clubBookId,
            page: p,
            totalPages: hasValidTotal ? effectiveTotalPages : undefined,
          });
        }
      }}
    >
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={0}
          max={hasValidTotal ? effectiveTotalPages : undefined}
          className="min-w-0 flex-1 rounded-md px-3 py-1.5 text-sm transition-all focus:outline-none"
          style={{
            border: "1px solid var(--color-border)",
            background: "var(--color-card)",
            color: "var(--color-foreground)",
          }}
          placeholder={t("progress.pagePlaceholder")}
          value={page}
          onChange={(e) => setPage(e.target.value)}
          onFocus={(e) => (e.currentTarget.style.borderColor = "var(--color-accent)")}
          onBlur={(e) => (e.currentTarget.style.borderColor = "var(--color-border)")}
        />
        <span className="text-xs flex-shrink-0" style={{ color: "var(--color-muted-foreground)" }}>
          {t("progress.ofLabel")}
        </span>
        <input
          type="number"
          min={1}
          className="min-w-0 flex-1 rounded-md px-3 py-1.5 text-sm transition-all focus:outline-none"
          style={{
            border: "1px solid var(--color-border)",
            background: "var(--color-card)",
            color: "var(--color-foreground)",
          }}
          placeholder={t("progress.totalPagesPlaceholder")}
          value={totalPages}
          onChange={(e) => setTotalPages(e.target.value)}
          onFocus={(e) => (e.currentTarget.style.borderColor = "var(--color-accent)")}
          onBlur={(e) => (e.currentTarget.style.borderColor = "var(--color-border)")}
        />
      </div>
      <Button type="submit" size="sm" loading={update.isPending} className="w-full">
        {t("progress.updateButton")}
      </Button>
      <p className="text-xs" style={{ color: "var(--color-muted-foreground)", opacity: 0.7 }}>
        {t("progress.editionHint")}
      </p>
      {update.error && (
        <p className="text-xs" style={{ color: "var(--color-destructive)" }}>
          {update.error.message}
        </p>
      )}
    </form>
  );
}
